
import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { warehouseService } from '../api';
import { WarehouseLocation } from '../api/types';
import { useLoading } from '../context/LoadingContext';

// Fix default marker icon issue with Leaflet + bundlers
const defaultIcon = L.icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

interface WarehouseMapProps {
    isOpen: boolean;
    onClose: () => void;
}

const WarehouseMap: React.FC<WarehouseMapProps> = ({ isOpen, onClose }) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<L.Map | null>(null);
    const { withLoading } = useLoading();
    const [warehouses, setWarehouses] = useState<Record<string, WarehouseLocation>>({});
    const [error, setError] = useState<string>('');

    // Fetch warehouse locations
    useEffect(() => {
        if (!isOpen) return;
        const fetchWarehouses = async () => {
            try {
                const resp = await withLoading(() => warehouseService.getWarehouseLocations());
                if (resp.status === 200 && resp.data) {
                    setWarehouses(resp.data);
                    setError('');
                } else {
                    setError('Failed to load warehouse locations');
                }
            } catch {
                setError('Error fetching warehouse locations');
            }
        };
        fetchWarehouses();
    }, [isOpen]);

    // Initialize and update map
    useEffect(() => {
        if (!isOpen || !mapRef.current) return;

        // Small delay to ensure DOM is ready
        const timer = setTimeout(() => {
            if (!mapRef.current) return;

            // Destroy previous map instance
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }

            const map = L.map(mapRef.current).setView([22.5937, 78.9629], 5); // India center
            mapInstanceRef.current = map;

            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
                maxZoom: 19,
            }).addTo(map);

            // Add warehouse markers
            const entries = Object.entries(warehouses);
            if (entries.length > 0) {
                const bounds = L.latLngBounds([]);
                entries.forEach(([id, wh]) => {
                    const marker = L.marker([wh.latitude, wh.longitude]).addTo(map);
                    marker.bindPopup(
                        `<div style="font-family: var(--ih-font-family-primary); padding: 4px 0;">
              <strong style="font-size: 13px;">${wh.name}</strong><br/>
              <span style="font-size: 11px; color: var(--ih-theme-text-muted);">
                ${wh.latitude.toFixed(4)}, ${wh.longitude.toFixed(4)}
              </span>
            </div>`
                    );
                    bounds.extend([wh.latitude, wh.longitude]);
                });
                map.fitBounds(bounds, { padding: [40, 40] });
            }

            // Force resize
            setTimeout(() => map.invalidateSize(), 100);
        }, 50);

        return () => {
            clearTimeout(timer);
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, [isOpen, warehouses]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="rounded-2xl shadow-2xl w-[90vw] max-w-4xl h-[80vh] max-h-[700px] flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--ih-theme-bg-card)', borderWidth: '1px', borderColor: 'var(--ih-theme-border-light)' }}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4" style={{ borderBottomWidth: '1px', borderColor: 'var(--ih-theme-border-light)', backgroundColor: 'var(--ih-theme-bg-secondary)' }}>
                    <div>
                        <h2 className="text-lg font-bold" style={{ color: 'var(--ih-theme-text-primary)', fontFamily: 'var(--ih-font-family-heading)' }}>Warehouse Locations</h2>
                        <p className="text-xs" style={{ color: 'var(--ih-theme-text-tertiary)' }}>
                            {Object.keys(warehouses).length} warehouses found
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
                        style={{ color: 'var(--ih-theme-text-muted)' }}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Map */}
                <div className="flex-1 relative">
                    {error ? (
                        <div className="flex items-center justify-center h-full">
                            <p className="text-sm" style={{ color: 'var(--ih-theme-error)' }}>{error}</p>
                        </div>
                    ) : (
                        <div ref={mapRef} className="w-full h-full" />
                    )}
                </div>
            </div>
        </div>
    );
};

export default WarehouseMap;
